/**
 * foregroundGeoBridge.ts — FASE 1 (Luglio 2026)
 *
 * WRAPPER DI COMPATIBILITÀ → backgroundGeoBridge.ts
 *
 * Questo file esiste SOLO per retrocompatibilità con i consumer esistenti
 * (useRouteRecording, RecordRoute, nativeGeolocation) che importano
 * `{ ForegroundGeo } from '@/services/foregroundGeoBridge'`.
 *
 * TUTTA la logica reale è in backgroundGeoBridge.ts, che usa il plugin
 * funzionante @capacitor-community/background-geolocation.
 */

import {
  ForegroundGeo as _ForegroundGeo,
  start,
  stop,
  configure,
  isTracking,
  getTrackingStatus,
  getPendingStats,
  syncPendingLocations,
  ackPendingFlush,
  requestBatteryOptimizationExemption,
  isAvailable,
  isGpsAvailable,
  setGpsAvailable,
  addDistanceKm,
  onFlush,
} from './backgroundGeoBridge';

// ── Healthcheck periodico ────────────────────────────────────────────────

let _healthcheckInterval: ReturnType<typeof setInterval> | null = null;
let _consecutiveHealthcheckFailures = 0;

export function startForegroundGeoHealthcheck(
  onStatusChange?: (alive: boolean, failures: number) => void,
): void {
  if (_healthcheckInterval) return;
  _healthcheckInterval = setInterval(async () => {
    const alive = isAvailable();
    if (!alive) _consecutiveHealthcheckFailures++;
    else _consecutiveHealthcheckFailures = 0;
    onStatusChange?.(alive, _consecutiveHealthcheckFailures);
  }, 30_000);
}

export function stopForegroundGeoHealthcheck(): void {
  if (_healthcheckInterval) { clearInterval(_healthcheckInterval); _healthcheckInterval = null; }
}

export function isForegroundGeoAlive(): boolean {
  return _consecutiveHealthcheckFailures < 3;
}

export async function pingForegroundGeoNow(): Promise<boolean> {
  return isAvailable();
}

// ── Oggetto ForegroundGeo (API compatibile con tutti i consumer) ─────────

export const ForegroundGeo = {
  start: (callback?: any) => start(callback),
  stop: () => stop(),
  configure: (opts: any) => configure(opts),
  isTracking: () => getTrackingStatus().then((s: any) => s.tracking),
  isTrackingDetailed: () => getTrackingStatus(),
  getPendingStats: () => getPendingStats(),
  syncPendingLocations: () => syncPendingLocations(),
  ackPendingFlush: (ids: any) => ackPendingFlush(ids),
  requestBatteryOptimizationExemption: () => requestBatteryOptimizationExemption(),
  isBatteryOptimizationExempted: async () => ({ exempted: true, message: 'Foreground Service Android' }),
  addListener: (event: string, callback: (data: any) => void) => {
    if (event === 'position') return _ForegroundGeo.addListener(event, callback);
    return { remove: () => {} };
  },
  removeAllListeners: async () => {},
  startHealthcheck: startForegroundGeoHealthcheck,
  stopHealthcheck: stopForegroundGeoHealthcheck,
  isAlive: isForegroundGeoAlive,
  pingNow: pingForegroundGeoNow,
};

export { isAvailable, isGpsAvailable, setGpsAvailable, addDistanceKm, onFlush };
export { BackgroundGeo } from './backgroundGeoBridge';
